// ─── SHARED OFF-PALETTE BACKGROUND CONSTANTS ─────────────────────────────────
// Tones that sit outside the three-level BG_SHELL / BG_CONTENT / BG_SURFACE
// hierarchy. Documented in GRC_UI_PATTERNS.md §21 "Color system".

export const BG_SUBTLE   = '#f3f2ef';  // table headers, tag chips, disabled inputs
export const BG_HOVER_LT = '#f5f3ef';  // lighter hover variant (ObjectivesView, Dashboard)
export const BG_TABLE    = '#f9f8f5';  // table alternating rows (ContextView, ScopeView)
export const BG_ACTIVE   = '#ebebeb';  // active nav item in SettingsView sidebar

// ─── BACKGROUND TONE SYSTEM (§21) ─────────────────────────────────────────────
export const BG_SHELL        = '#fafafa';  // Navbar, Sidebar, page frame (off-white — palette test)
export const BG_SHELL_HOVER  = '#f0f0f0';  // Hover state on sidebar items
export const BG_CONTENT      = '#fcfcfc';  // Main content area — pure white (palette test); also list-row hover background
export const BG_SURFACE      = '#fafafa';  // Cards, panels, forms, list rows, ConfirmDialog (currently matches BG_SHELL — palette test)
export const BG_MUTED        = '#f3f4f6';  // Badge fallback background when label not in colorMap
export const SURFACE_WHITE   = '#ffffff';  // Pure white — input/form backgrounds, text on dark-filled buttons and active toggles, table bodies, selected-chip text

// ─── BRAND AND TEXT TOKENS (§21) ──────────────────────────────────────────────
export const BRAND_PRIMARY       = '#1a5c3a';                  // brand green
export const BRAND_PRIMARY_DARK  = '#166534';                  // darker brand green, text on light-green bgs
export const BRAND_PRIMARY_TINT  = 'rgba(26,92,58,0.06)';      // active-item wash
export const TEXT_HEADING        = '#0f2235';                  // headings, module titles, sidebar groups
export const TEXT_DEFAULT        = '#374151';                  // body text, field values
export const TEXT_MUTED          = '#6b7280';                  // secondary text, captions, inactive items
export const TEXT_DISABLED       = '#9ca3af';                  // disabled inputs, placeholder-like text
export const BORDER_DEFAULT      = '#d6d2ca';                  // dividers, ConfirmDialog border, sidebar border
export const TOOLTIP_BG          = '#4b5563';                  // tooltip background (§24)
export const TOOLTIP_TEXT        = '#f9fafb';                  // tooltip text (§24)

// ─── SEMANTIC STATE TOKENS (§21) ──────────────────────────────────────────────
export const UNSAVED_AMBER       = '#b45309';  // unsaved-changes label text (§4)
export const ERROR_BG            = '#fee2e2';  // error banner background
export const ERROR_TEXT          = '#991b1b';  // error banner text
export const ERROR_BORDER        = '#fca5a5';  // error banner border
export const FIELD_ERROR_BORDER  = '#dc2626';  // red border on a failing field

// ─── STATUS PALETTE (§21 — for Badge colorMaps) ───────────────────────────────
export const STATUS_GOOD     = { bg: '#dcfce7', text: '#166534' };  // LOW risk, Achieved, Implemented, Approved, Closed (good)
export const STATUS_WARN     = { bg: '#fef3c7', text: '#92400e' };  // MEDIUM, On Track, At Risk, In Progress
export const STATUS_BAD      = { bg: '#fee2e2', text: ERROR_TEXT };  // HIGH, Not Met, Not implemented, Overdue
export const STATUS_NEUTRAL  = { bg: '#e5e7eb', text: TEXT_DEFAULT };  // Not Started, Draft, Under Review, n/a
export const STATUS_INFO     = { bg: '#dbeafe', text: '#1e40af' };  // Planned, Informational

// ─── BUTTON STYLE CONSTANTS ────────────────────────────────────────────────────
// Inline style objects — spread into React button style props.
// Never hardcode button colors inline; always import and use these constants.
// See GRC_UI_PATTERNS.md §21 "Color system" for the full usage table.

export const BTN_PRIMARY = {
  background: BRAND_PRIMARY,
  color: SURFACE_WHITE,
};

export const BTN_SECONDARY = {
  background: 'transparent',
  border: '1px solid #d6d2ca',
  color: TEXT_DEFAULT,
};

export const BTN_DESTRUCTIVE_OUTLINE = {
  background: 'transparent',
  border: '1px solid #fca5a5',
  color: ERROR_TEXT,
};

export const BTN_DESTRUCTIVE_FILLED = {
  background: '#dc2626',
  color: SURFACE_WHITE,
};

// BTN_SAVE_AND_LEAVE shares BTN_PRIMARY's fill but is a distinct semantic
// token: the green "Save and leave" action in the unsaved-changes guard (§5).
// Kept separate so future visual changes to one do not affect the other.
export const BTN_SAVE_AND_LEAVE = {
  background: BRAND_PRIMARY,
  color: SURFACE_WHITE,
};

export const BTN_STATUS_FORWARD = {
  background: 'transparent',
  border: '1px solid #86efac',
  color: '#166534',
};

export const BTN_STATUS_FLAG = {
  background: 'transparent',
  border: '1px solid #fcd34d',
  color: '#92400e',
};

// ─── TYPOGRAPHY TOKENS (§26) ────────────────────────────────────────────────
// Spreadable style objects. Spread into React style props to set fontFamily,
// fontSize and fontWeight in one shot. Non-font properties (color, margin,
// letterSpacing, textTransform, etc.) remain inline alongside the spread.
// See GRC_UI_PATTERNS.md §26 "Typography tokens" for the full usage table.

// Two typeface families, each defined once. Every font token below references
// one of these constants, so the product typeface can be changed from a single
// point. FONT_FAMILY_DISPLAY is Syne (page/section titles, body, buttons, list
// rows); FONT_FAMILY_MONO is DM Mono (labels, helper text, micro-labels).
export const FONT_FAMILY_DISPLAY = "'Syne', sans-serif";
export const FONT_FAMILY_MONO    = "'DM Mono', monospace";

export const FONT_PAGE_TITLE    = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.31rem' };
export const FONT_SECTION_TITLE = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.21rem' };
export const FONT_BODY          = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 400, fontSize: '0.91rem' };
export const FONT_BODY_STRONG   = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 600, fontSize: '0.91rem' };
export const FONT_LABEL         = { fontFamily: FONT_FAMILY_MONO,    fontWeight: 400, fontSize: '0.84rem' };
export const FONT_SMALL         = { fontFamily: FONT_FAMILY_MONO,    fontWeight: 400, fontSize: '0.78rem' };
export const FONT_TINY          = { fontFamily: FONT_FAMILY_MONO,    fontWeight: 400, fontSize: '0.71rem' };
export const FONT_BUTTON        = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 600, fontSize: '0.88rem' };
export const FONT_LIST_ITEM     = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 400, fontSize: '0.88rem' };

// Stat-value pattern (Syne 700 / 1.1rem / lineHeight 1.1) currently duplicated
// inline across twelve view files; migrated to this token in a later step.
export const FONT_STAT_VALUE    = { fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.1 };
